import { useEffect, useState } from 'react'

import { system, type HealthResponse, type ReadyResponse } from '../api/endpoints'
import { messageFor } from '../lib/errors'

/**
 * Proves the whole path end to end: browser → nginx → backend → Postgres.
 *
 * If this page loads and shows a reachable database, then routing, the API proxy, the
 * session token and the migrations are all working. It is the first thing to look at
 * when a deploy is suspect.
 */
export function DashboardPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [ready, setReady] = useState<ReadyResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    Promise.all([system.health(), system.ready()])
      .then(([h, r]) => {
        if (cancelled) return
        setHealth(h)
        setReady(r)
      })
      .catch((caught) => {
        if (!cancelled) setError(messageFor(caught))
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <section className="stack">
      <header className="page-header">
        <h1>Dashboard</h1>
        <p className="muted">Where the shipment pipeline will live. For now, a health check.</p>
      </header>

      {error && <p className="alert alert-error">{error}</p>}

      <div className="grid">
        <div className="card">
          <h2>Service</h2>
          <dl className="kv">
            <dt>Status</dt>
            <dd>
              {health ? <span className="pill pill-ok">serving</span> : <span className="pill">…</span>}
            </dd>
            <dt>Version</dt>
            <dd>{health?.version ?? '—'}</dd>
            <dt>Environment</dt>
            <dd>{health?.env ?? '—'}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>Data</h2>
          <dl className="kv">
            <dt>Database</dt>
            <dd>
              {ready ? (
                <span className="pill pill-ok">{ready.database}</span>
              ) : (
                <span className="pill">…</span>
              )}
            </dd>
          </dl>
          <p className="muted small">
            A reachable database means the schema is migrated and the connection pool is live.
          </p>
        </div>
      </div>

      <div className="card">
        <h2>Next</h2>
        <p className="muted">
          The wedge is the operational loop: rates → quote → booking → documents → tracking →
          invoice → payment. Customers is the first hub record; shipments come next.
        </p>
      </div>
    </section>
  )
}
